import { useCallback } from 'react';

import { AxiosResponse } from 'axios';

import { useSuspenseQuery } from '@tanstack/react-query';
import type {
  TypeGetWorkLocationsCommutingMethodsData,
  TypeGetWorkLocationsCommutingMethodsResponse,
} from '~/api/workLocations';
import { registerMockAPI } from '~/mocks/mockApis';

import { axiosInstance } from '~/services/fetchServiceV2';

export type CommutingMethodItem = TypeGetWorkLocationsCommutingMethodsData[number];

type UseGetCommutingMethodsParams = {
  workLocationId: string;
};

export const COMMUTE_METHODS_QUERY_KEY = 'commuting-methods';

/** @description 근무지별 출근 방법 목록 조회 */
export const useGetCommuteMethods = (params: UseGetCommutingMethodsParams) => {
  const handleResponse = useCallback((response: AxiosResponse<TypeGetWorkLocationsCommutingMethodsResponse>) => {
    return response.data.data;
  }, []);

  const { data } = useSuspenseQuery({
    queryKey: [COMMUTE_METHODS_QUERY_KEY, params.workLocationId],
    queryFn: async () => {
      const endpoint = `/work-locations/${params.workLocationId}/commuting-methods`;
      return await axiosInstance.get<TypeGetWorkLocationsCommutingMethodsResponse>(endpoint);
    },
    gcTime: 0,
    retry: 1,
    select: (response) => handleResponse(response),
  });

  return { data };
};

registerMockAPI<TypeGetWorkLocationsCommutingMethodsResponse>({
  method: 'get',
  endpoint: '/work-locations/:workLocationId/commuting-methods',
  label: '출근 방법 선택/출근 방법 목록 조회',
  scenarios: [
    {
      name: '출근 방법 목록 (다중)',
      response: {
        code: '0000',
        message: '성공',
        data: [
          { commutingMethod: { code: 'SHUTTLE', text: '셔틀' }, parkingGuide: null },
          { commutingMethod: { code: 'CAR', text: '자차' }, parkingGuide: '주차장 B동 지하 1층 이용' },
          { commutingMethod: { code: 'SELF', text: '대중교통 및 기타' }, parkingGuide: null },
        ],
      },
    },
    {
      name: '출근 방법 목록 (단일)',
      response: {
        code: '0000',
        message: '성공',
        data: [{ commutingMethod: { code: 'SHUTTLE', text: '셔틀' }, parkingGuide: null }],
      },
    },
    {
      name: '출근 방법 목록 없음',
      response: {
        code: '0000',
        message: '성공',
        data: [],
      },
    },
    {
      name: '서버 에러',
      response: { code: '500', message: '서버 내부 오류가 발생했습니다.', data: [] },
      status: 500,
    },
  ],
});
